import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Modal from '../common/Modal';
import ConfirmModal from '../common/ConfirmModal';
import { TicketTypeIcon } from '../common/TicketTypeSelector';
import api from '../../services/api';
import './ProjectTaskTypeManagement.css';

/**
 * ProjectTaskTypeManagement - Admin panel for managing task types available to a project
 * Allows assigning/removing custom task types from a project scope
 */
const ProjectTaskTypeManagement = ({ projectId }) => {
  const [allTaskTypes, setAllTaskTypes] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: null,
    isLoading: false
  });

  // Fetch all available task types and project-specific assignments
  const fetchTaskTypes = useCallback(async () => {
    setLoading(true);
    try {
      const [allTypesRes, projectTypesRes] = await Promise.all([
        api.get('/task-types/dropdown'),
        api.get(`/projects/${projectId}/task-types`)
      ]);

      setAllTaskTypes(allTypesRes.data.items || []);
      setProjectTypes(projectTypesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch task types:', error);
      toast.error('Failed to load task types');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchTaskTypes();
    }
  }, [projectId, fetchTaskTypes]);

  // Check if a type is assigned to the project
  const isTypeAssigned = (typeId) => {
    return projectTypes.some(pt => pt.task_type_id === typeId);
  };

  // Handle assigning a type to the project
  const handleAssignType = async (typeId) => {
    setUpdating(true);
    try {
      await api.post(`/projects/${projectId}/task-types/${typeId}`);
      toast.success('Task type assigned to project');
      await fetchTaskTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign task type');
    } finally {
      setUpdating(false);
    }
  };

  // Handle removing a type from the project
  const handleUnassignType = (typeId) => {
    const type = allTaskTypes.find(t => t.id === typeId);
    setConfirmModal({
      isOpen: true,
      title: 'Remove Task Type',
      message: `Are you sure you want to remove "${type?.name}" from this project? Tasks of this type will no longer be assignable.`,
      confirmText: 'Remove',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await api.delete(`/projects/${projectId}/task-types/${typeId}`);
          toast.success('Task type removed from project');
          await fetchTaskTypes();
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to remove task type');
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  // Handle showing type details
  const handleShowTypeInfo = (type) => {
    setSelectedType(type);
    setShowInfoModal(true);
  };

  if (loading) {
    return <div className="task-type-management loading">Loading task types...</div>;
  }

  // Separate system defaults and custom types
  const systemTypes = allTaskTypes.filter(t => t.is_system_default);
  const customTypes = allTaskTypes.filter(t => !t.is_system_default);

  return (
    <div className="task-type-management">
      <div className="management-intro">
        <p>
          Select which task types are available for use in this project. System types (Epic, Story, Bug, Task, Subtask)
          are available by default. You can also restrict available types by unchecking them below.
        </p>
      </div>

      <div className="tt-table-wrap">
        <table className="tt-table">
          <thead>
            <tr>
              <th className="tt-th-icon" />
              <th>Name</th>
              <th>Category</th>
              <th>Key</th>
              <th>Status</th>
              <th className="tt-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* System Default section */}
            <tr className="tt-section-row">
              <td colSpan={6}>System Default Types</td>
            </tr>
            {systemTypes.map(type => (
              <tr key={type.id} className="tt-row">
                <td className="tt-td-icon">
                  <TicketTypeIcon type={type.key} size="sm" config={{ icon: type.icon_url || '', color: '#4c9aff', bgColor: '#deebff', label: type.name }} />
                </td>
                <td className="tt-name">{type.name}</td>
                <td><span className="tt-badge tt-badge--system">System Default</span></td>
                <td><code className="tt-key">{type.key}</code></td>
                <td>
                  {isTypeAssigned(type.id)
                    ? <span className="tt-status-active">Active</span>
                    : <span className="tt-status-inactive">Inactive</span>}
                </td>
                <td className="tt-td-actions">
                  <button className="tt-info-btn" onClick={() => handleShowTypeInfo(type)} title="View details">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  </button>
                  {!isTypeAssigned(type.id) && (
                    <button className="btn btn-xs btn-secondary" onClick={() => handleAssignType(type.id)} disabled={updating}>Enable</button>
                  )}
                </td>
              </tr>
            ))}

            {/* Custom Types section */}
            {customTypes.length > 0 && (
              <tr className="tt-section-row">
                <td colSpan={6}>Custom Types</td>
              </tr>
            )}
            {customTypes.map(type => (
              <tr key={type.id} className={`tt-row${isTypeAssigned(type.id) ? ' tt-row--assigned' : ''}`}>
                <td className="tt-td-icon">
                  <TicketTypeIcon type={type.id} size="sm" config={{ icon: type.icon_url || '', color: '#4c9aff', bgColor: '#deebff', label: type.name }} />
                </td>
                <td className="tt-name">{type.name}</td>
                <td><span className="tt-badge tt-badge--custom">Custom</span></td>
                <td>
                  {isTypeAssigned(type.id)
                    ? <code className="tt-key">{type.key}</code>
                    : <span className="tt-key-empty">Not assigned</span>}
                </td>
                <td>
                  {isTypeAssigned(type.id)
                    ? <span className="tt-status-active">Active</span>
                    : null}
                </td>
                <td className="tt-td-actions">
                  <button className="tt-info-btn" onClick={() => handleShowTypeInfo(type)} title="View details">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  </button>
                  {isTypeAssigned(type.id) ? (
                    <button className="btn btn-xs btn-danger" onClick={() => handleUnassignType(type.id)} disabled={updating}>Remove</button>
                  ) : (
                    <button className="btn btn-xs btn-secondary" onClick={() => handleAssignType(type.id)} disabled={updating}>Assign</button>
                  )}
                </td>
              </tr>
            ))}

            {customTypes.length === 0 && (
              <tr className="tt-empty-row">
                <td colSpan={6}>No custom task types available. Admins can create custom types in the main settings.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Type Details Modal */}
      <Modal
        isOpen={showInfoModal}
        onClose={() => {
          setShowInfoModal(false);
          setSelectedType(null);
        }}
        title={selectedType?.name}
      >
        {selectedType && (
          <div className="type-info-modal">
            <div className="info-section">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Type ID:</span>
                  <code className="info-value">{selectedType.id}</code>
                </div>
                <div className="info-item">
                  <span className="info-label">Key:</span>
                  <code className="info-value">{selectedType.key}</code>
                </div>
                <div className="info-item">
                  <span className="info-label">Category:</span>
                  <span className="info-value">
                    {selectedType.is_subtask_type ? 'Subtask Type' : 'Regular Type'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status:</span>
                  <span className="info-value">
                    {isTypeAssigned(selectedType.id) ? (
                      <span className="status-badge active">Assigned to Project</span>
                    ) : (
                      <span className="status-badge inactive">Not Assigned</span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {selectedType.icon_url && (
              <div className="info-section">
                <span className="info-label">Icon:</span>
                <div className="icon-preview">
                  <img src={selectedType.icon_url} alt={selectedType.name} />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Confirmation Modal */}
      <ConfirmModal {...confirmModal} />
    </div>
  );
};

export default ProjectTaskTypeManagement;
